import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Translation } from 'react-i18next';
import { Auth } from 'aws-amplify';
import InputMask from 'react-input-mask';
import { Prompt } from 'react-router-dom';
import classNames from 'classnames';
import { cloneDeep, get, isEmpty } from 'lodash';
import { socialLoginIcons } from './authUtil.js';
import {
  setUser,
  setAuthStatus,
  getTopMatches,
  getSchools,
  registerUser,
  saveResults,
  setLoading,
  checkUser,
  setPreRegistrationFlag,
  setUserIPAddress,
  setRegistrationInProgress,
} from '../../store/actions';
import {
  getAuthState,
  getOnboardingState,
  getTenantSSOFlag,
  getEducationLevelSelector,
  getUserCountry,
  getPreRegistrationState,
  getUserTerritory,
  getUserEmail,
  getProviderName,
  getCognitoUsername,
} from '../../store/selectors';
import { getFederationName } from './federationNameStrings.js';
import { getIPAddress } from '../../util/userIPAddressUtil';
import RegistrationType from './RegistrationType.jsx';
import ErrorMessage from '../ErrorMessage';
import HighSchoolInputs from './HighSchoolInputs';
import ChangeRegistrationTypeLink from './ChangeRegistrationType';
import LockIcon from '../utility/LockIcon.jsx';
import validate from '../utility/CheckError';
import { fireGTM } from '../../gtm/gtmUtil';
import { registrationPage_GTM } from '../../gtm/datalayers/registrationDataLayers.json';
import eyeIcon from '../../ASUPhotos/icons/eyeIcon.png';
import eyeCrossIcon from '../../ASUPhotos/icons/eyeCrossIcon.png';
import { education } from '../../copy/onboardingCopy.json';
import Loading from '../utility/Loading';

class RegisterForm extends Component {
  _isMounted = false;

  constructor(props) {
    super(props);
    this.state = {
      firstRender: false,
      fname: '',
      lname: '',
      email: '',
      dob: '',
      password: '',
      highSchool: '',
      customSchool: '',
      graduationYear: '',
      receiveCommunications: false,
      confirmPass: '',
      userExists: false,
      inputType: 'text',
      errorsPresent: false,
      errors: {
        fname: false,
        lname: false,
        emailInUse: false,
        emailInvalid: false,
        emailEmpty: false,
        dob: false,
        password: false,
        confirmPass: false,
      },
      isLoading: true,
      // awful name, but it's standard to react-transition-group
      inProp: true,
      passwordIsMasked: true,
      passwordConfirmIsMasked: true,
    };
  }

  get primary() {
    return this.props.tenant.primary;
  }

  get errors() {
    return this.state.errors;
  }

  get passwordIsMasked() {
    return this.state.passwordIsMasked;
  }

  get passwordConfirmIsMasked() {
    return this.state.passwordConfirmIsMasked;
  }

  get onboarding() {
    return this.props.onboarding;
  }

  get tenantHasHighSchoolFeature() {
    return this.props.tenant.features.includes('highschool');
  }

  get color() {
    return this.props.color;
  }

  get auth() {
    return this.props.auth;
  }

  get isAuthenticated() {
    return this.auth.isAuthenticated;
  }

  get firstName() {
    return get(this.auth, 'user.attributes.given_name');
  }

  get lastName() {
    return get(this.auth, 'user.attributes.family_name');
  }

  get email() {
    return get(this.auth, 'user.attributes.email') || this.props.userEmail;
  }

  get preregistration() {
    return this.props.preregistration;
  }

  get registrationType() {
    return this.preregistration.registerOptionsValue;
  }

  get tenantHasSSO() {
    return this.props.tenantSSOFlag;
  }

  get registrationIsEmail() {
    return this.registrationType === 'email';
  }

  get registrationIsFederation() {
    return this.tenantHasSSO && this.registrationType === 'federation';
  }

  get registrationIsSocial() {
    return this.registrationType === 'social';
  }

  get onboardingUserData() {
    return this.props.userData;
  }

  get authToken() {
    return this.props.auth.jwtToken;
  }

  get authUser() {
    return this.props.auth.user;
  }

  get userEducationLevel() {
    return this.props.userEducationLevel;
  }

  get userCountry() {
    return this.props.userCountry;
  }

  get tenantName() {
    return this.props.tenant && this.props.tenant.name;
  }

  get providerName() {
    return this.props.providerName;
  }

  get cognitoUsername() {
    return this.props.cognitoUsername;
  }

  componentDidMount() {
    this._isMounted = true;
    if (this.isAuthenticated && this.email && this.registrationIsSocial) {
      const event = {
        target: {
          id: 'new-email',
        },
      };
      this.handleErrors(event, this.email, false, false);
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  togglePasswordMask = () => {
    if (this._isMounted) {
      this.setState((prevState) => ({
        passwordIsMasked: !prevState.passwordIsMasked,
      }));
    }
  };

  keyDownPasswordMask = (e) => {
    if (['Enter'].includes(e.key)) {
      this.togglePasswordMask();
    }
  };

  togglePasswordMaskConfirm = () => {
    if (this._isMounted) {
      this.setState((prevState) => ({
        passwordConfirmIsMasked: !prevState.passwordConfirmIsMasked,
      }));
    }
  };

  keyDownPasswordMaskConfirm = (e) => {
    if (['Enter'].includes(e.key)) {
      this.togglePasswordMaskConfirm();
    }
  };

  blurDateOfBirth = (event, dob) => {
    const regex = /(\d\d)(\d\d)(\d\d\d\d)/gi;
    const newDob = dob.replace(regex, '$3-$1-$2');

    if (this._isMounted) {
      this.setState({
        dob: newDob,
      });
    }

    this.handleErrors(event, newDob);
  };

  handleInputFocus = (obj, e) => {
    e.preventDefault();

    // Is this the first onFocus change AND is the focused input field anything other than email?
    if (!this.state.firstRender && this._isMounted) {
      this.setState(
        {
          firstRender: true,
        },
        // We need to send whatever input onFocus this initially was to GTM \
        // unless the first input field focused was 'email'
        () => {
          return obj.text !== 'email' && fireGTM(obj);
        }
      );
    }

    // All ofther onFocus events get sent to GTM
    else if (this.state.firstRender) {
      fireGTM(obj);
    }
  };

  onInputChange = async (event) => {
    event.persist();

    if (this._isMounted) {
      if (event.target.id === 'new-email') {
        this.setState({
          email: event.target.value.toLowerCase(),
        });
      } else if (event.target.id === 'confirmPass') {
        this.setState(
          {
            [event.target.id]: event.target.value,
          },
          () => {
            this.handleErrors(event, this.state.confirmPass);
          }
        );
      } else if (event.target.id === 'password') {
        this.setState(
          {
            [event.target.id]: event.target.value,
          },
          () => {
            this.handleErrors(event, this.state.password);
          }
        );
      } else {
        this.setState({
          [event.target.id]: event.target.value,
        });
      }
    }
  };

  cognitoSignUp = async () => {
    let session, user;
    const { email, password } = this.state;

    try {
      this.props.setLoading(true);
      await Auth.signUp({
        username: email,
        password,
      });
      user = await Auth.signIn(email, password);
      session = await Auth.currentSession();
      await this.props.setAuthStatus({ authenticated: true, session });
      await this.saveUserInfo(session.idToken.jwtToken);
      await this.props.setUser(user);
    } catch (error) {
      console.error(error);

      return error;
    }
  };

  handleCheckbox = async () => {
    fireGTM(registrationPage_GTM.leanMoreCheckBox);

    if (this._isMounted) {
      this.setState((prevState) => ({
        ...prevState,
        receiveCommunications: !prevState.receiveCommunications,
      }));
    }
  };

  saveGameResults = async (token) => {
    const requestBody = {
      riasecValues: this.props.preregistration.score,
    };

    await this.props.saveResults(requestBody, token);
  };

  saveUserInfo = async (token) => {
    try {
      const userSchool =
        this.state.highSchool === 'Other'
          ? this.state.customSchool
          : this.state.highSchool;
      const requestBody = {
        email: this.state.email ? this.state.email : this.email,
        firstName: this.state.fname ? this.state.fname : this.firstName,
        lastName: this.state.lname ? this.state.lname : this.lastName,
        dateOfBirth: this.state.dob,
        highSchool: userSchool,
        graduationYear: this.state.graduationYear,
        country: this.props.preregistration.country,
        state: this.props.preregistration.territory,
        educationLevel: this.props.preregistration.education.educationLevel,
        receiveCommunications: this.state.receiveCommunications,
        registrationSource: this.props.preregistration.registrationSource,
      };

      const registeredUserId = await this.props.registerUser(
        requestBody,
        token
      );
      // Get the user IP address
      const userIPAddress = await getIPAddress();

      if (!isEmpty(userIPAddress) && registeredUserId) {
        this.props.setUserIPAddress(registeredUserId, token, userIPAddress);
      }
      await this.saveGameResults(token);
    } catch (error) {
      console.log('Registration error: ', error);
    }
  };

  handleErrors = async (event, inputValue, initialState, checkEvent = true) => {
    if (checkEvent) event.persist();

    const errorsCopy = cloneDeep(this.state.errors);
    const stateCopy = cloneDeep(this.state);
    const checkEmailExists =
      event.target.id === 'new-email' && inputValue.length
        ? await this.props.checkUser(inputValue)
        : false;
    const error = await validate(
      event.target.id,
      inputValue,
      errorsCopy,
      stateCopy,
      initialState,
      checkEmailExists
    );

    if (this._isMounted) {
      this.setState(
        () => ({
          errors: { ...error },
        }),
        () => {
          this.checkErrors();
        }
      );
    }
  };

  checkErrors = () => {
    const { errors } = this.state;
    let pageHasErrors;

    if (Object.values(errors).indexOf(true) > -1) {
      pageHasErrors = true;
    } else {
      pageHasErrors = false;
    }

    if (this._isMounted) {
      this.setState({
        errorsPresent: pageHasErrors,
      });
    }
  };

  handleSubmit = async (event) => {
    this.props.setRegistrationInProgress(true);
    event.preventDefault();
    this.props.setPreRegistrationFlag(false);
    if (this.registrationIsEmail) {
      // We first need to tell GTM that the user submitted their registration data
      fireGTM(registrationPage_GTM.continueClicked);
      await this.cognitoSignUp();
    } else if (this.isAuthenticated) {
      await this.saveUserInfo(this.authToken);
      await this.props.setUser(this.authUser);
    }
    // Now tell GTM there was a succesful signup/registration
    fireGTM(registrationPage_GTM.continueSuccessful);
    this.props.setPreRegistrationFlag(true);

    this.props.setInPropInParent(false);
  };

  buttonisDisabled = () => {
    const educationLevel = this.props.userEducationLevel;
    const country = this.props.userCountry;

    if (
      this.registrationIsEmail &&
      (!this.state.fname ||
        !this.state.lname ||
        !this.state.email ||
        !this.state.dob ||
        !this.state.password ||
        !this.state.confirmPass ||
        this.state.errorsPresent)
    ) {
      return true;
    }

    if (
      (this.registrationIsFederation || this.registrationIsSocial) &&
      (!this.state.dob || this.state.errorsPresent)
    ) {
      return true;
    }

    if (educationLevel === education.degree2) {
      // US based HS students
      if (
        country === 'United States' &&
        this.tenantHasHighSchoolFeature &&
        (!this.state.highSchool ||
          (this.state.highSchool === 'Other' && !this.state.customSchool) ||
          !this.state.graduationYear)
      ) {
        return true;
      }
      // International HS students
      if (
        country !== 'United States' &&
        this.tenantHasHighSchoolFeature &&
        !this.state.graduationYear
      ) {
        return true;
      }
    }

    return false;
  };

  handleHighSchoolSelected = (school) => {
    if (this._isMounted) {
      this.setState({
        highSchool: school.value,
      });
    }
  };

  handleYearSelected = (year) => {
    if (this._isMounted) {
      this.setState({
        graduationYear: year.value,
      });
    }
  };

  checkTenantSSOFeatures = () => {
    return (
      <>
        <ChangeRegistrationTypeLink
          primary={this.primary}
          setRegisterPage={this.props.setRegisterPage}
        />
        <RegistrationType
          registrationType={this.registrationType}
          tenantName={this.tenantName}
        />
      </>
    );
  };

  emailExistsErrorMessage = () => {
    let externalSSOProvider =
      this.cognitoUsername &&
      this.cognitoUsername.substr(0, this.cognitoUsername.indexOf('_'));

    if (externalSSOProvider) {
      externalSSOProvider = socialLoginIcons.includes(externalSSOProvider)
        ? externalSSOProvider.charAt(0).toUpperCase() +
          externalSSOProvider.slice(1)
        : externalSSOProvider.toUpperCase();
    }

    return `${`Email already exists ${
      externalSSOProvider ? `associated with ${externalSSOProvider}` : ''
    }`}`;
  };

  render() {
    const labelRegistrationTextPrimary = {
      color: `${this.primary}`,
    };

    const primarycheckBox = {
      border: `1px solid ${this.props.tenant.primary}`,
      color: `${this.props.tenant.primary}`,
    };

    return (
      <Translation>
        {(t) => (
          <div className="main-right-content">
            {this.props.auth.registrationInProgress ? (
              <Loading height="100%" />
            ) : (
              <>
                <Prompt
                  message={t(
                    'Warning! Your responses will be lost. Do you want to continue?'
                  )}
                />
                <div className="registration-type-container">
                  {this.checkTenantSSOFeatures()}
                </div>
                <form id="registration-form" autocomplete="off">
                  <div className="registration-form-row">
                    <div className="form-label-error-input">
                      <div className="label-and-error">
                        <label
                          className="label-registration registration-form-column"
                          htmlFor="fname"
                          style={labelRegistrationTextPrimary}
                        >
                          {t('First name')}
                        </label>
                        {this.registrationIsEmail ? null : (
                          <LockIcon propsClassName="input-form" />
                        )}
                        <div
                          className="error-register"
                          id={`fname-error-div-${t('Field required')}`}
                        >
                          <span
                            data-cy="fname-error"
                            id={`fname-error-${t('Field required')}`}
                            style={{
                              display: this.errors.fname ? '' : 'none',
                            }}
                          >
                            <ErrorMessage error={t('Field required')} />
                          </span>
                        </div>
                      </div>
                      <input
                        onBlur={(event) => {
                          this.handleErrors(event, this.state.fname);
                        }}
                        type="text"
                        onChange={this.onInputChange}
                        onFocus={(e) =>
                          this.handleInputFocus(
                            registrationPage_GTM.firstName,
                            e
                          )
                        }
                        disabled={
                          this.registrationIsFederation ||
                          this.registrationIsSocial
                        }
                        placeholder={
                          this.registrationIsEmail ? t('Name') : this.firstName
                        }
                        className={
                          this.errors.fname
                            ? 'form-register error'
                            : 'form-register'
                        }
                        id="fname"
                        data-cy="fname"
                      />
                    </div>
                    <div className="form-label-error-input">
                      <div className="label-and-error">
                        <label
                          className="label-registration registration-form-column"
                          htmlFor="lname"
                          style={labelRegistrationTextPrimary}
                        >
                          {t('Last name')}
                        </label>
                        {this.registrationIsEmail ? null : (
                          <LockIcon propsClassName="input-form" />
                        )}

                        <span
                          data-cy="lname-error"
                          id={`lname-error-${t('Field required')}`}
                          style={{
                            display: this.errors.lname ? '' : 'none',
                          }}
                        >
                          <ErrorMessage error={t('Field required')} />
                        </span>
                      </div>
                      <input
                        type="text"
                        onBlur={(event) => {
                          this.handleErrors(event, this.state.lname);
                        }}
                        onFocus={(e) =>
                          this.handleInputFocus(
                            registrationPage_GTM.lastName,
                            e
                          )
                        }
                        onChange={this.onInputChange}
                        disabled={
                          this.registrationIsFederation ||
                          this.registrationIsSocial
                        }
                        placeholder={
                          this.registrationIsEmail ? t('Name') : this.lastName
                        }
                        className={
                          this.errors.lname
                            ? 'form-register error'
                            : 'form-register'
                        }
                        id="lname"
                        data-cy="lname"
                      />
                    </div>
                  </div>
                  <div className="registration-form-row second">
                    <div className="form-label-error-input">
                      <div className="label-and-error">
                        <label
                          className="label-registration registration-form-column"
                          htmlFor="new-email"
                          style={labelRegistrationTextPrimary}
                        >
                          {t('Email')}
                        </label>
                        {this.registrationIsEmail ? null : (
                          <LockIcon propsClassName="input-form" />
                        )}
                        <span
                          id="email-error-in-use"
                          data-cy="email-error-in-use"
                          style={{
                            display: this.errors.emailInUse ? '' : 'none',
                          }}
                        >
                          <ErrorMessage
                            error={this.emailExistsErrorMessage()}
                            duplicateSSOEmail={this.registrationIsSocial}
                          />
                        </span>
                        <span
                          id="email-error-invalid"
                          data-cy="email-error-invalid"
                          style={{
                            display: this.errors.emailInvalid ? '' : 'none',
                          }}
                        >
                          <ErrorMessage error={t('Email invalid')} />
                        </span>
                        <span
                          id="email-error-empty"
                          data-cy="email-error-empty"
                          style={{
                            display: this.errors.emailEmpty ? '' : 'none',
                          }}
                        >
                          <ErrorMessage error={t('Field required')} />
                        </span>
                      </div>
                      <input
                        onBlur={(event) => {
                          this.handleErrors(event, this.state.email);
                        }}
                        onFocus={(e) =>
                          this.handleInputFocus(registrationPage_GTM.email, e)
                        }
                        type="text"
                        onInput={this.onInputChange}
                        autoComplete="off"
                        disabled={
                          this.registrationIsFederation ||
                          this.registrationIsSocial
                        }
                        placeholder={
                          this.registrationIsEmail ? t('Email') : this.email
                        }
                        className={
                          this.errors.emailEmpty ||
                          this.errors.emailInUse ||
                          this.errors.emailInvalid
                            ? 'form-register error'
                            : 'form-register'
                        }
                        id="new-email"
                        data-cy="new-email"
                      />
                    </div>
                    <div className="form-label-error-input">
                      <div className="label-and-error">
                        <label
                          className="label-registration registration-form-column"
                          htmlFor="dob"
                          style={labelRegistrationTextPrimary}
                        >
                          {t('Date of birth')}
                        </label>
                        <span
                          id="dob-error"
                          data-cy="dob-error"
                          style={{
                            display: this.errors.dob ? '' : 'none',
                          }}
                        >
                          <ErrorMessage error={t('Birthday invalid')} />
                        </span>
                      </div>
                      <InputMask
                        onFocus={(e) => {
                          this.handleInputFocus(registrationPage_GTM.dob, e);
                        }}
                        placeholder="mm/dd/yyyy"
                        id="dob"
                        data-cy="dob"
                        className={
                          this.errors.dob
                            ? 'form-register error'
                            : 'form-register'
                        }
                        mask="99/99/9999"
                        maskPlaceholder="mm/dd/yyyy"
                        onChange={this.onInputChange}
                        onBlur={(event) => {
                          this.blurDateOfBirth(event, this.state.dob);
                        }}
                        type="text"
                        autoComplete="new-password"
                      />
                    </div>
                  </div>
                  {this.registrationIsEmail ? (
                    <div
                      className={
                        this.state.highSchool !== 'Other'
                          ? 'registration-form-row'
                          : 'registration-form-row extra-space'
                      }
                    >
                      <div className="form-label-error-input">
                        <label
                          className="label-registration registration-form-column padding-bottom"
                          htmlFor="password"
                          style={labelRegistrationTextPrimary}
                        >
                          {t('Password')}
                        </label>
                        <div className="registration-form-password">
                          <input
                            type={this.passwordIsMasked ? 'password' : 'text'}
                            onChange={(event) => {
                              this.onInputChange(event);
                            }}
                            placeholder="•••••••••"
                            className={
                              this.errors.password
                                ? 'form-register error'
                                : 'form-register'
                            }
                            id="password"
                            autocomplete="new-password"
                            data-cy="password"
                          />
                          <img
                            className="password-eye register"
                            onClick={this.togglePasswordMask}
                            onKeyDown={this.keyDownPasswordMask}
                            src={this.passwordIsMasked ? eyeIcon : eyeCrossIcon}
                            alt="eye"
                            tabIndex="0"
                            role="button"
                            aria-label={t('view password button')}
                            aria-pressed={!this.passwordIsMasked}
                          />
                        </div>
                      </div>
                      <div className="form-label-error-input">
                        <label
                          className="label-registration registration-form-column padding-bottom"
                          htmlFor="confirmPass"
                          style={labelRegistrationTextPrimary}
                        >
                          {t('Confirm password')}
                        </label>
                        <div className="registration-form-password">
                          <input
                            type={
                              this.passwordConfirmIsMasked ? 'password' : 'text'
                            }
                            onChange={(event) => {
                              this.onInputChange(event);
                            }}
                            placeholder="•••••••••"
                            className={
                              this.errors.confirmPass
                                ? 'form-register error'
                                : 'form-register'
                            }
                            id="confirmPass"
                            data-cy="confirmPass"
                          />
                          <img
                            className="password-eye register"
                            onClick={this.togglePasswordMaskConfirm}
                            onKeyDown={this.keyDownPasswordMaskConfirm}
                            src={
                              this.passwordConfirmIsMasked
                                ? eyeIcon
                                : eyeCrossIcon
                            }
                            alt="eye"
                            tabIndex="0"
                            role="button"
                            aria-label={t('view password button')}
                            aria-pressed={!this.passwordConfirmIsMasked}
                          />
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div
                    className={`registration-form-row
              ${
                !this.registrationIsEmail
                  ? 'registration-form-row--horizontal'
                  : 'registration-form-row--stacked'
              }`}
                  >
                    {this.registrationIsEmail ? null : <LockIcon />}
                    <p
                      className={`"password-requriements" ${
                        this.registrationIsEmail ? null : 'federated-info'
                      }`}
                      id="password-requirements"
                    >
                      {this.registrationIsEmail
                        ? t(
                            '* At least 8 characters, including a lowercase letter, an uppercase letter, and a number.'
                          )
                        : t(
                            'This information is not editable and was obtained via {{providerName}}',
                            {
                              providerName: getFederationName(
                                this.tenantName.toUpperCase()
                              ),
                            }
                          )}
                    </p>
                    <span
                      id="password-error"
                      data-cy="password-error"
                      style={{
                        display: this.errors.password ? '' : 'none',
                      }}
                    >
                      <ErrorMessage
                        error={t(
                          'Please enter the password in the correct format.'
                        )}
                      />
                    </span>
                    <span
                      id="confirm-password-error"
                      data-cy="confirm-password-error"
                      style={{
                        display: this.errors.confirmPass ? '' : 'none',
                      }}
                    >
                      <ErrorMessage error={t('Passwords do not match')} />
                    </span>
                  </div>
                  <div className="registration-form-row">
                    <div className="form-label-error-input form-label-error-input--full-width-mobile">
                      <div className="checkbox-and-label">
                        <input
                          type="checkbox"
                          id="receive-communication"
                          data-cy="receive-communication"
                          onChange={this.handleCheckbox}
                          checked={this.state.checked}
                          style={primarycheckBox}
                        />
                        <label
                          htmlFor="receive-communication"
                          className="checkbox-label"
                        >
                          {t(
                            'I would like to learn more about relevant educational opportunities.'
                          )}
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="registration-form-line-break" />
                  <div className="registration-form-row">
                    {this.tenantHasHighSchoolFeature ? (
                      <HighSchoolInputs
                        onboarding={this.onboarding}
                        errors={this.state.errors}
                        highSchool={this.state.highSchool}
                        customSchool={this.state.customSchool}
                        graduationYear={this.state.graduationYear}
                        handleErrors={this.handleErrors}
                        primary={this.primary}
                        handleHighSchoolSelected={this.handleHighSchoolSelected}
                        handleYearSelected={this.handleYearSelected}
                        onInputChange={this.onInputChange}
                        userEducationLevel={this.userEducationLevel}
                        userCountry={this.userCountry}
                        advisorDashboard={false}
                      />
                    ) : null}
                  </div>
                  <button
                    disabled={this.buttonisDisabled()}
                    type="submit"
                    className={classNames(
                      'button--primary',
                      'registration-form-submit',
                      this.color
                    )}
                    id="submit-button"
                    data-cy="submit-button"
                    onClick={this.handleSubmit}
                  >
                    {t('Continue to career results')}
                  </button>
                </form>
              </>
            )}
          </div>
        )}
      </Translation>
    );
  }
}

const mapStateToProps = (state) => {
  const tenant = state.tenant;
  const onboarding = getOnboardingState(state);
  const preregistration = getPreRegistrationState(state);
  const auth = getAuthState(state);
  const tenantSSOFlag = getTenantSSOFlag(state);
  const userEducationLevel = getEducationLevelSelector(state);
  const userCountry = getUserCountry(state);
  const userTerritory = getUserTerritory(state);
  const userEmail = getUserEmail(state);
  const providerName = getProviderName(state);
  const cognitoUsername = getCognitoUsername(state);

  return {
    onboarding,
    preregistration,
    tenant,
    auth,
    tenantSSOFlag,
    userEducationLevel,
    userCountry,
    userTerritory,
    userEmail,
    providerName,
    cognitoUsername,
  };
};

export default connect(mapStateToProps, {
  setAuthStatus,
  setUser,
  getTopMatches,
  getSchools,
  registerUser,
  saveResults,
  setLoading,
  checkUser,
  setPreRegistrationFlag,
  setUserIPAddress,
  setRegistrationInProgress,
})(RegisterForm);
